import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { SearchService } from '../../../common/services/search.service';
import { SiteService } from '../../../common/services/site.service';
import { Router } from '@angular/router';
import { AuthService } from '../../../common/services/auth.service';
import { AppSettings } from '../../../common/constants/appSettings.constants';
import { StrapiService } from '../../../common/services/strapi.service';
import { environment } from '../../../../environments/environment';
import { ContentType } from '../../../common/models/site.model';

@Component({
  selector: 'app-nav-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent implements OnInit {
  theme: string = 'dark-mode';

  private destroyRef = inject(DestroyRef);
  searchInputChanged$: Subject<string> = new Subject<string>();

  settings = AppSettings;
  searchInput: string = '';
  isMac: boolean = false;
  mobileSearchDisplay: boolean = false;
  mobileNavOpen = false;
  currentNav: any[] = [];
  currentRoute = "/";
  loggedIn = false;
  isClientLibraryFlyoutVisible = false;
  librariesByType: { [type: string]: any[] } = {};
  hoveredLib: number | null = null;
  displayRecipes: boolean = environment.displayRecipes;
  displayDocs: boolean = environment.displayDocs;
  contentType: ContentType = 'api';
  homepageRoute: string = '/';
  private navInitialized = false;
  

  constructor(private searchService: SearchService,
    private siteService: SiteService,
    private authService: AuthService,
    private router: Router,
    private strapiService: StrapiService
  ) {
    if (navigator.userAgent.includes('Mac')) {
      this.isMac = true;
    }
  }

  checkRoute() {
    this.currentRoute = this.router.url;
  }

  // Method to trigger sidebar toggle
  menuOpen() {
    this.siteService.openSidebar();
    this.mobileSearchDisplay = false;
  }

  menuClose() {
    this.siteService.closeSidebar();
    this.mobileSearchDisplay = false;
  }

  ngOnInit() {

    this.searchInputChanged$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((input: string) => {
        this.searchService.setSearchQuery(input);
      });
   

    // Load nav once and mark as ready
    this.siteService.currentNavEndpoints$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((nav: any[]) => {
        this.currentNav = nav;
        this.navInitialized = true;
        this.checkRoute(); 
      });
    
    this.siteService.contentType$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((contentType: ContentType) => {
        this.contentType = contentType;
        this.homepageRoute = contentType === 'api' ? '/' : '/docs';
        // console.log('Content type updated in header:', this.contentType);
      });

    // Route change handler
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.navInitialized) {
          this.checkRoute();
        }
      });

    this.siteService.navOpen$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.mobileNavOpen = state;
      });

    this.searchService.searchQuery$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(term => {
        this.searchInput = term;
        if (term === '') this.searchInputChanged$.next('');
      });

    this.authService.loggedIn$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(loggedIn => {
        this.loggedIn = loggedIn;
      });

    this.siteService.theme$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((theme: any) => {
          this.theme = theme;
          return this.strapiService.getAllCodelibraries();
        })
      )
      .subscribe(data => {
        this.librariesByType = data?.reduce((grouped: any, lib: any) => {
          const type = lib.Type || '';
          grouped[type] = grouped[type] || [];
          grouped[type].push(lib);
          return grouped;
        }, {});
      });

  }

  submitSearchInput() {
    this.searchInputChanged$.next(this.searchInput);
  }

  onNavChange(event: Event) {
    const selectedRoute = (event.target as HTMLSelectElement).value;
    this.router.navigate([selectedRoute]);
  }

  stopSearching() {
    this.searchService.setSearching(false);
  }

  doLogout(): void {
    this.stopSearching();
    this.authService.logout();
  }

  toggleTheme(): void {
    this.siteService.toggleTheme();
  }

  getThemeIcons(lib: any): { Default: any; Hover: any } {
    return {
      Default: lib.IconDefault,
      Hover: this.theme === 'light-mode' ? lib.IconLight : lib.IconDark
    };
  }


}
